/* eslint-disable camelcase */
import * as uuid from 'uuid';
import signature, {
  SignatureUploadResponse,
} from 'signer-app/context/signer-app-client/signature';
import { FromSignaturesData } from 'signer-app/signature-modal/signature-modal-context/signature-provider-impl';
import { drWhoSignature } from 'signer-app/signer-experience/story-data';

type SignatureMeta = FromSignaturesData;

type SignatureNamespace = ReturnType<typeof signature>;

export default class MockSignature implements SignatureNamespace {
  #signatures: Array<SignatureMeta> = [];

  #initials: Array<SignatureMeta> = [];

  #files = new Map<
    SignatureMeta['signature_id'],
    {
      url: string;
    }
  >();

  constructor() {
    // In these kinds of mocks, it's useful to go ahead name things instead of
    // leaving them as random IDs.
    this.#files.set('doctor-who', {
      url: drWhoSignature,
    });
  }

  fetchSignatures = async (type: 'S' | 'I') => {
    if (type === 'S') {
      return {
        count: this.#signatures.length,
        list: this.#signatures,
      };
    } else {
      return {
        count: this.#initials.length,
        list: this.#initials,
      };
    }
  };

  async fetchSignatureByGuid(): Promise<any> {
    throw new Error('Not implemented');
  }

  removeSignature = async (guid: string): Promise<any> => {
    const signatureIndex = this.#signatures.findIndex(
      (s) => s.signature_id === guid,
    );
    const initialIndex = this.#initials.findIndex(
      (s) => s.signature_id === guid,
    );

    if (signatureIndex) {
      this.#signatures.splice(signatureIndex, 1);
    }
    if (initialIndex) {
      this.#initials.splice(initialIndex, 1);
    }
    return { success: true, data: { status: 'ok' }, error: null };
  };

  async rotateSignature() {
    throw new Error('Not implemented');
  }

  uploadCanvas: SignatureNamespace['uploadCanvas'] = async (
    sigData,
  ): Promise<SignatureUploadResponse> => {
    const x: SignatureMeta = {
      signature_id: uuid.v4(),
      width: 139,
      height: 60,
      created_at: Math.floor(Date.now() / 1000).toString(),
      last_used_at: null,
      create_type_code: 'C',
      type_code: sigData.type_code,
      signature: sigData.signature,
    } as const;
    this.#files.set(x.signature_id, {
      // This is a data:image/png;base64
      url: sigData.signature.image,
    });
    if (sigData.type_code === 'S') {
      this.#signatures.push(x);
    } else {
      this.#initials.push(x);
    }

    return {
      is_valid: true,
      signature_id: x.signature_id,
      signature_guid: x.signature_id,
      width: 139,
      height: 60,
    };
  };

  uploadSignatureFile: SignatureNamespace['uploadSignatureFile'] = async (
    sigData,
  ) => {
    if (sigData.createType === 'U') {
      const image: string = await new Promise((resolve) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(sigData.file);
        fileReader.onload = () => {
          resolve(fileReader.result as string);
        };
      });

      const tmp = await this.upload({
        type_code: sigData.type,
        create_type_code: sigData.createType,
        signature: {
          image,
        },
        guid: '',
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        degrees: 0,
        is_cropped: false,
        threshold: 0,
      });

      return tmp;
    }
    throw new Error(`Unsupported createType: ${sigData.createType}`);
  };

  /**
   * This function was originally written by co-pilot:
   * Use a canvas to write the text in the font and return the data:image/png;base64
   */
  #signatureGenerator = async (text: string, font: string) => {
    let canvas;
    let ctx;
    try {
      // even inside this try/catch calling getContext logs an error, making the
      // logs noisy.
      if (typeof jest !== 'undefined' && !window.IS_STORYBOOK) {
        throw new Error("JSDOM doesn't implementat Canvas");
      }
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d');
      if (typeof ctx?.measureText === 'undefined') {
        throw new Error('Looks like a test environment');
      }
      if (!ctx) {
        throw new Error('No context');
      }
    } catch (e) {
      return {
        height: 60,
        width: 140,
        image: `data:text/plain, font: ${font}  text: ${text}`,
      };
    }
    const padding = 100;

    // It looked very pixelated at 60px
    canvas.height = 60;
    canvas.width = 139;
    const fontStyle = `${canvas.height}px ${font}`;
    // The font has to be set before measuring the tet
    ctx.font = fontStyle;
    let metrics = null;
    metrics = ctx.measureText(text);

    // And then chaning the canvas size resets the font
    canvas.width = metrics.width + padding + padding;
    canvas.height =
      metrics.actualBoundingBoxAscent +
      metrics.actualBoundingBoxDescent +
      padding +
      padding;
    // so it has to be set again
    ctx.font = fontStyle;
    ctx.fillText(text, padding, padding + metrics.actualBoundingBoxAscent);

    ctx.font = '10px sans-serif';
    ctx.fillStyle = '#900';

    return {
      image: canvas.toDataURL(),
      width: canvas.width,
      height: canvas.height,
    };
  };

  upload: SignatureNamespace['upload'] = async (
    sigData,
  ): Promise<SignatureUploadResponse> => {
    const x: SignatureMeta = {
      signature_id: uuid.v4(),
      width: 139,
      height: 60,
      created_at: Math.floor(Date.now() / 1000).toString(),
      last_used_at: null,
      create_type_code: 'C',
      type_code: sigData.type_code,
      signature: sigData.signature,
    };

    let signatureUrl;
    if (
      sigData.create_type_code === 'T' &&
      sigData.signature.text &&
      sigData.signature.font_family
    ) {
      const { image } = await this.#signatureGenerator(
        sigData.signature.text,
        sigData.signature.font_family,
      );
      signatureUrl = image;
    } else {
      signatureUrl = sigData.signature.image;
    }

    this.#files.set(x.signature_id, {
      // This is a data:image/png;base64
      url: signatureUrl,
    });
    if (sigData.type_code === 'S') {
      this.#signatures.push(x);
    }

    return {
      is_valid: true,
      signature_guid: x.signature_id,
      signature_id: x.signature_id,
      width: 139,
      height: 60,
    };
  };

  signatureURL = (url: string, guid: string) => {
    const file = this.#files.get(guid);
    return file?.url ?? url;
  };
}
